import { Box, Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordFamiliarizationEditItem } from "../../../models/recordFamiliarization/recordFamiliarizationEditItem";
import * as whistleBlowService from "../../../models/services/whistleBlowService";
import * as storeActions from "../../../models/store/storeActions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IWhistleBlowCategoryDialogProp {
    classes?: string;
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IWhistleBlowCategoryDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    activeState: number;
    item: IRecordFamiliarizationEditItem;
}

export class WhistleBlowCategoryDialog extends React.Component<IWhistleBlowCategoryDialogProp, IWhistleBlowCategoryDialogState> {
    private orgStateHash: string = "";

    constructor(props: IWhistleBlowCategoryDialogProp) {
        super(props);
        this.state = {
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            activeState: 0,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => whistleBlowService.getWhistleBlowCategoryEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.familiarization;
        const state = {
            ...this.state,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            activeState: item.activeState
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IWhistleBlowCategoryDialogProp, prevState: IWhistleBlowCategoryDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    getSaveDataFromState = (props: IWhistleBlowCategoryDialogProp, state: IWhistleBlowCategoryDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("activeState", state.activeState);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => whistleBlowService.saveWhistleBlowCategoryEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveWhistleBlowCategoryParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        const isNew = state.isNew;
        return (
            <Dialog
                id="wb-category-dialog"
                maxWidth="sm"
                title={isNew ? Translations.AddWhistleBlowCategory : (Translations.WhistleBlowCategory + ": " + state.item.code)}
                body={
                    <Box display="flex" flexDirection="column">
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} fullWidth required>
                                    <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 100 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description} fullWidth>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title="" fullWidth>
                                    <FormControlLabel
                                        label={Translations.InUse}
                                        control={
                                            <Checkbox
                                                title={Translations.InUse}
                                                checked={state.activeState > 0}
                                                onChange={(_e, checked: boolean) => {
                                                    this.setState({ activeState: checked ? 1 : 0 });
                                                }}
                                            />
                                        }
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}